import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView,
  TouchableWithoutFeedback,
  Keyboard,
  Platform,
  Image,
  TouchableOpacity,
} from 'react-native';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../apiConfig.js';

export default function CreateAccount() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    email: '',
    businessName: '',
    businessLogo: null, // will store a data URL string for web
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [errorMessage, setErrorMessage] = useState('');

  // small helper to set fields like onChangeText('firstName', text)
  const setField = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  // Web image picker using a hidden input[type=file]
  const openFilePicker = () => fileInputRef.current?.click();
  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      // store as data URL so your existing JSON API can receive a string
      setFormData((p) => ({ ...p, businessLogo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleCreateAccount = async () => {
    setErrorMessage('');

    try {
      const confirmationToken = Math.random().toString(36).slice(2, 12);
      const confirmationExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

      const payload = {
        phone: formData.phone,
        password: formData.password,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        businessName: formData.businessName,
        businessLogo: formData.businessLogo, // data URL string
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        user_confirmed: false,
        confirmation_token: confirmationToken,
        confirmation_expires_at: confirmationExpiresAt,
      };

      const response = await axios.post(`${API_BASE_URL}/create-account`, payload);

      const code = response?.data?.code;

      if (code === 'EXPIRED_TOKEN') {
        alert('Your confirmation token has expired. Please request a new one.');
        const resendResponse = await axios.post(`${API_BASE_URL}/resend-token`, { email: formData.email });
        if (resendResponse.status === 200) {
          alert('A new confirmation token has been sent to your email.');
        } else {
          alert('Failed to resend confirmation token. Please try again later.');
        }
      } else if (code === 'PENDING_CONFIRMATION') {
        alert('Your email confirmation is still pending. Please check your email.');
        navigate('/login', { replace: true });
      } else if (code === 'ACCOUNT_CREATED') {
        alert('Account created successfully. Please check your email to confirm your account.');
        navigate('/login', { replace: true });
      } else if (code === 'ALREADY_REGISTERED') {
        alert('You are already a registered user. Please log in.');
        navigate('/login', { replace: true });
      } else if (code === 'NEW_TOKEN_SENT') {
        alert('A new confirmation email has been sent. Please confirm your email.');
        navigate('/login', { replace: true });
      } else {
        alert('Failed to create account on the server.');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      setErrorMessage('Failed to create account. Please try again later.');
      alert('Failed to create account. Please try again later.');
    }
  };

  return (
    <div style={styles.pageContainer}>
      <div style={styles.backgroundOverlay}>
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          {/* Hidden file input for web image selection */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={onFilePicked}
          />

          <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
            <ScrollView
              contentContainerStyle={{
                flexGrow: 1,
                justifyContent: 'center',
                alignItems: 'center',
                padding: 20,
                minHeight: '100vh',
              }}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              <View style={styles.container}>
                <View style={styles.logoContainer}>
                  <Text style={styles.logoText}>TradeBook</Text>
                  <Text style={styles.logoSubtext}>Business Management</Text>
                </View>

                <Text style={styles.header}>Create Your Account</Text>
                <Text style={styles.subheader}>Join TradeBook to manage your business</Text>

                {!!errorMessage && (
                  <View style={styles.errorContainer}>
                    <Text style={styles.error}>{errorMessage}</Text>
                  </View>
                )}

                <View style={styles.formSection}>
                  <Text style={styles.sectionTitle}>Personal Information</Text>

                  <View style={styles.inputRow}>
                    <View style={styles.inputHalf}>
                      <Text style={styles.label}>First Name</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="First Name"
                        placeholderTextColor="#9CA3AF"
                        value={formData.firstName}
                        onChangeText={(t) => setField('firstName', t)}
                      />
                    </View>
                    <View style={styles.inputHalf}>
                      <Text style={styles.label}>Last Name</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="Last Name"
                        placeholderTextColor="#9CA3AF"
                        value={formData.lastName}
                        onChangeText={(t) => setField('lastName', t)}
                      />
                    </View>
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Phone Number</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="(555) 123-4567"
                      placeholderTextColor="#9CA3AF"
                      value={formData.phone}
                      onChangeText={(t) => setField('phone', t)}
                    />
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Email Address</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="you@example.com"
                      placeholderTextColor="#9CA3AF"
                      value={formData.email}
                      onChangeText={(t) => setField('email', t)}
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Password</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="Create a secure password"
                      placeholderTextColor="#9CA3AF"
                      value={formData.password}
                      onChangeText={(t) => setField('password', t)}
                      secureTextEntry
                    />
                  </View>
                </View>

                <View style={styles.formSection}>
                  <Text style={styles.sectionTitle}>Business Information</Text>
                  <Text style={styles.sectionSubtitle}>Optional - for invoicing and quotes</Text>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Business Name</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="Your Business Name"
                      placeholderTextColor="#9CA3AF"
                      value={formData.businessName}
                      onChangeText={(t) => setField('businessName', t)}
                    />
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Business Logo</Text>
                    <TouchableOpacity style={styles.logoButton} onPress={openFilePicker}>
                      <Text style={styles.logoButtonText}>
                        {formData.businessLogo ? 'Change Logo' : 'Select Logo'}
                      </Text>
                    </TouchableOpacity>
                    {!!formData.businessLogo && (
                      <View style={styles.logoPreviewContainer}>
                        <Image source={{ uri: formData.businessLogo }} style={styles.logoPreview} />
                        <Text style={styles.logoPreviewText}>Logo selected</Text>
                      </View>
                    )}
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Business Address</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="123 Business Street"
                      placeholderTextColor="#9CA3AF"
                      value={formData.address}
                      onChangeText={(t) => setField('address', t)}
                    />
                  </View>

                  <View style={styles.inputRow}>
                    <View style={styles.inputThird}>
                      <Text style={styles.label}>City</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="City"
                        placeholderTextColor="#9CA3AF"
                        value={formData.city}
                        onChangeText={(t) => setField('city', t)}
                      />
                    </View>
                    <View style={styles.inputThird}>
                      <Text style={styles.label}>State</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="State"
                        placeholderTextColor="#9CA3AF"
                        value={formData.state}
                        onChangeText={(t) => setField('state', t)}
                      />
                    </View>
                    <View style={styles.inputThird}>
                      <Text style={styles.label}>Zip Code</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="12345"
                        placeholderTextColor="#9CA3AF"
                        value={formData.zip}
                        onChangeText={(t) => setField('zip', t)}
                        keyboardType="number-pad"
                      />
                    </View>
                  </View>
                </View>

                <TouchableOpacity style={styles.createAccountButton} onPress={handleCreateAccount}>
                  <Text style={styles.createAccountButtonText}>Create Account</Text>
                </TouchableOpacity>

                <View style={styles.divider}>
                  <View style={styles.dividerLine} />
                  <Text style={styles.dividerText}>or</Text>
                  <View style={styles.dividerLine} />
                </View>

                <TouchableOpacity style={styles.cancelButton} onPress={() => navigate('/login')}>
                  <Text style={styles.cancelButtonText}>Back to Sign In</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </TouchableWithoutFeedback>
        </KeyboardAvoidingView>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  backgroundOverlay: {
    flex: 1,
    backgroundColor: '#fff',
  },
  container: {
    width: '100%',
    maxWidth: 800, // Made much wider - was 400px
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 40, // Increased padding for better proportions
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  logoText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  logoSubtext: {
    fontSize: 16,
    color: '#6B7280',
  },
  header: {
    fontSize: 32, // Increased font size
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
    color: '#333',
  },
  subheader: {
    fontSize: 18, // Increased font size
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 30, // Increased spacing
  },
  errorContainer: {
    backgroundColor: '#FEE2E2',
    padding: 10,
    borderRadius: 5,
    marginBottom: 15,
  },
  error: {
    color: '#B91C1C',
    textAlign: 'center',
  },
  formSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 20, // Increased font size
    fontWeight: 'bold',
    marginBottom: 15, // Increased spacing
    color: '#333',
  },
  sectionSubtitle: {
    fontSize: 16, // Increased font size
    color: '#6B7280',
    marginBottom: 15, // Increased spacing
  },
  inputRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  inputHalf: {
    width: '48%',
  },
  inputThird: {
    width: '30%',
  },
  fieldContainer: {
    marginBottom: 20, // Increased spacing
  },
  label: {
    marginBottom: 8, // Increased spacing
    fontWeight: 'bold',
    color: '#333',
    fontSize: 16, // Added font size
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    padding: 15, // Increased padding
    borderRadius: 8, // Larger border radius
    color: '#333',
    backgroundColor: '#F9FAFB',
    fontSize: 16, // Added font size
  },
  logoButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    alignItems: 'center',
    marginBottom: 10,
  },
  logoButtonText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  logoPreviewContainer: {
    alignItems: 'center',
    marginTop: 10,
  },
  logoPreview: {
    width: 100,
    height: 100,
    borderRadius: 10,
  },
  logoPreviewText: {
    marginTop: 5,
    color: '#6B7280',
  },
  createAccountButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 15,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 20,
  },
  createAccountButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#D1D5DB',
  },
  dividerText: {
    marginHorizontal: 10,
    color: '#6B7280',
  },
  cancelButton: {
    backgroundColor: '#FF3B30',
    paddingVertical: 15,
    borderRadius: 5,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
});
